import { AppState, AppAction } from './types';
import { APP_CONFIG } from '../constants';
import { SimulatorCartItem } from '../features/simulator/types';

// Helper function to load from localStorage safely
const loadFromLocalStorage = <T>(key: string, defaultValue: T): T => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.warn(`Failed to load ${key} from localStorage:`, error);
    return defaultValue;
  }
};

// Helper function to save to localStorage safely
const saveToLocalStorage = <T>(key: string, value: T): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn(`Failed to save ${key} to localStorage:`, error);
  }
};

// Migration function to add balneotherapy field to existing cart items
const ensureBalneotherapyField = (items: any[]): SimulatorCartItem[] => {
  return items.map(item => ({
    ...item,
    balneotherapy: item.balneotherapy || 'none'
  }));
};

try {
  localStorage.removeItem('favorites');
} catch {
  // ignore
}

const getInitialDarkMode = (): boolean => {
  try {
    const stored = localStorage.getItem('darkMode');
    if (stored !== null) {
      return JSON.parse(stored) === true;
    }
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
  } catch {
    // ignore
  }
  return false;
};

// Initial state with localStorage integration
export const initialAppState: AppState = {
  ui: {
    darkMode: getInitialDarkMode(),
    showMobileFilters: false,
    isArticlesCollapsed: false,
  },
  data: {
    acts: [],
    loading: true,
    error: null,
    allAvailablePathologyHashtags: [],
  },
  searchAndFilter: {
    searchQuery: '',
    filters: {
      lettre_cle: '',
      soumis_referentiel: 'all',
      theme: '',
      reeducation: '',
      selected_vignettes: [],
    },
    recentSearches: loadFromLocalStorage('recentSearches', []),
    cartItems: ensureBalneotherapyField(loadFromLocalStorage('cartItems', [])),
    currentPage: 1,
    displayCotationType: 'auto',
  },
  toast: {
    toasts: [],
  },
};

// Pure reducer function
export const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    // Data Actions
    case 'SET_ACTS':
      return {
        ...state,
        data: { ...state.data, acts: action.payload }
      };

    case 'SET_LOADING':
      return {
        ...state,
        data: { ...state.data, loading: action.payload }
      };

    case 'SET_ERROR':
      return {
        ...state,
        data: { ...state.data, error: action.payload }
      };

    case 'SET_PATHOLOGY_HASHTAGS':
      return {
        ...state,
        data: { ...state.data, allAvailablePathologyHashtags: action.payload }
      };

    // UI Actions
    case 'TOGGLE_DARK_MODE': {
      const newDarkMode = !state.ui.darkMode;
      saveToLocalStorage('darkMode', newDarkMode);
      return {
        ...state,
        ui: { ...state.ui, darkMode: newDarkMode }
      };
    }

    case 'SET_SHOW_MOBILE_FILTERS':
      return {
        ...state,
        ui: { ...state.ui, showMobileFilters: action.payload }
      };

    case 'SET_ARTICLES_COLLAPSED':
      return {
        ...state,
        ui: { ...state.ui, isArticlesCollapsed: action.payload }
      };

    // Search and Filter Actions
    case 'SET_SEARCH_QUERY':
      return {
        ...state,
        searchAndFilter: { ...state.searchAndFilter, searchQuery: action.payload }
      };

    case 'ADD_RECENT_SEARCH': {
      const newRecentSearches = [
        action.payload,
        ...state.searchAndFilter.recentSearches.filter(s => s !== action.payload)
      ].slice(0, APP_CONFIG.MAX_RECENT_SEARCHES);
      
      saveToLocalStorage('recentSearches', newRecentSearches);
      
      return {
        ...state,
        searchAndFilter: { 
          ...state.searchAndFilter, 
          recentSearches: newRecentSearches 
        }
      };
    }

    case 'SET_FILTERS':
      return {
        ...state,
        searchAndFilter: { ...state.searchAndFilter, filters: action.payload }
      };

    case 'SET_CURRENT_PAGE':
      return {
        ...state,
        searchAndFilter: { ...state.searchAndFilter, currentPage: action.payload }
      };

    case 'SET_DISPLAY_COTATION_TYPE':
      return {
        ...state,
        searchAndFilter: { ...state.searchAndFilter, displayCotationType: action.payload }
      };

    // New Cart Actions
    case 'TOGGLE_CART_ITEM': {
      const actToAdd = action.payload;
      const currentCartItems = state.searchAndFilter.cartItems;
      const existingItemIndex = currentCartItems.findIndex(item => item.act.ID === actToAdd.ID);

      let newCartItems;
      if (existingItemIndex !== -1) {
        newCartItems = currentCartItems.filter(item => item.act.ID !== actToAdd.ID);
      } else {
        const newItem: SimulatorCartItem = {
          id: `${actToAdd.ID}-${Date.now()}`,
          act: actToAdd,
          quantity: 1,
          homeVisitPercentage: 0
        };
        newCartItems = [...currentCartItems, newItem];
      }
      
      saveToLocalStorage('cartItems', newCartItems);
      return {
        ...state,
        searchAndFilter: { ...state.searchAndFilter, cartItems: newCartItems }
      };
    }

    case 'ADD_SIMULATOR_CART_ITEM': {
      const newItem = action.payload;
      const newCartItems = [...state.searchAndFilter.cartItems, newItem];
      saveToLocalStorage('cartItems', newCartItems);
      return {
        ...state,
        searchAndFilter: { ...state.searchAndFilter, cartItems: newCartItems }
      };
    }

    case 'REMOVE_SIMULATOR_CART_ITEM': {
      const itemIdToRemove = action.payload;
      const newCartItems = state.searchAndFilter.cartItems.filter(item => item.id !== itemIdToRemove);
      saveToLocalStorage('cartItems', newCartItems);
      return {
        ...state,
        searchAndFilter: { ...state.searchAndFilter, cartItems: newCartItems }
      };
    }

    case 'UPDATE_SIMULATOR_CART_ITEM_QUANTITY': {
      const { itemId, newQuantity } = action.payload;
      const newCartItems = state.searchAndFilter.cartItems.map(item =>
        item.id === itemId ? { ...item, quantity: newQuantity } : item
      );
      saveToLocalStorage('cartItems', newCartItems);
      return {
        ...state,
        searchAndFilter: { ...state.searchAndFilter, cartItems: newCartItems }
      };
    }

    case 'UPDATE_SIMULATOR_CART_ITEM_HOME_VISIT': {
      const { itemId, percentage } = action.payload;
      const newCartItems = state.searchAndFilter.cartItems.map(item =>
        item.id === itemId ? { ...item, homeVisitPercentage: percentage } : item
      );
      saveToLocalStorage('cartItems', newCartItems);
      return {
        ...state,
        searchAndFilter: { ...state.searchAndFilter, cartItems: newCartItems }
      };
    }

    case 'UPDATE_SIMULATOR_CART_ITEM_BALNEOTHERAPY': {
      const { itemId, balneotherapy } = action.payload;
      const newCartItems = state.searchAndFilter.cartItems.map(item =>
        item.id === itemId ? { ...item, balneotherapy } : item
      );
      saveToLocalStorage('cartItems', newCartItems);
      return {
        ...state,
        searchAndFilter: { ...state.searchAndFilter, cartItems: newCartItems }
      };
    }

    case 'CLEAR_SIMULATOR_CART': {
      saveToLocalStorage('cartItems', []);
      return {
        ...state,
        searchAndFilter: { ...state.searchAndFilter, cartItems: [] }
      };
    }

    // Toast Actions
    case 'ADD_TOAST':
      return {
        ...state,
        toast: { toasts: [...state.toast.toasts, action.payload] }
      };

    case 'REMOVE_TOAST':
      return {
        ...state,
        toast: { 
          toasts: state.toast.toasts.filter(toast => toast.id !== action.payload) 
        }
      };

    case 'CLEAR_ALL_TOASTS':
      return {
        ...state,
        toast: { toasts: [] }
      };

    default:
      return state;
  }
};